import React, { useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import SEO from "@/components/SEO";
import { useForm } from "react-hook-form";
import { UseFormProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ObjectSchema } from "yup";
import { ObjectShape } from "yup/lib/object";
import { yupResolver } from "@/utils/yup";
import { doLogin, DOING_LOGIN } from "@/store/login/loginActions";
import { toast } from "@/components/common/Toast";
import { useNavigate, useLocation } from "react-router";
import SignInForm from "./SignInForm";
import { LoginForm } from "./types";

const SignInContainerInner = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const location = useLocation();

  const urlParams = new URLSearchParams(location?.search);

  const redirectUrl = urlParams?.get("redirect_url")
    ? `${urlParams?.get("redirect_url")}${location?.hash || ""}`
    : null;

  const [isSubmitting, setSubmitting] = useState<boolean>(true);

  // TODO remove any type
  const { user_preference: userPreference } = useSelector((state: any) =>
    state.user.toJS()
  );

  const language = userPreference?.language === "ESP" ? "/es" : "";

  const { executeRecaptcha } = useGoogleReCaptcha();

  const { t } = useTranslation(["translation.login"]);

  const validationSchema: ObjectSchema<ObjectShape> = Yup.object().shape({
    email: Yup.string()
      .email(t("EnterValidEmail"))
      .required(t("EnterValidEmail")),
    password: Yup.string()
      .required(t("PasswordMinLength"))
      .min(6, t("PasswordMinLength")),
  });

  const formOptions: UseFormProps = {
    // onBlur, onChange, and etc...
    mode: "all",
    resolver: yupResolver(validationSchema),
  };

  const { register, reset, handleSubmit, formState, getValues } =
    useForm(formOptions);

  const { errors } = formState;

  const onError = (result: any) => {
    if (result.error === 401 || result.error === 422) {
      dispatch(toast.error(t("IncorrectEmailOrPassword", "0", "auth_failed")));
    } else if (result.error === 500) {
      dispatch(toast.error(t("ServerError")));
    }
  };

  const login = async (form: LoginForm) => {
    setSubmitting(true);
    dispatch({ type: DOING_LOGIN });
    try {
      if (!form || !form.email || !form.password) {
        console.error("Error While Login");
      } else {
        if (!executeRecaptcha) {
          dispatch(toast.error(t("ReCaptchaNotReady", "Please try again in a moment.")));
          return;
        }
        const reCaptcha = await executeRecaptcha("login");
        dispatch(
          doLogin({ ...form, reCaptcha }, onError, () => {
            if (redirectUrl) {
              navigate(redirectUrl);
            }
          })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SEO title={t("Login")}>
      <SignInForm
        redirectUrl={redirectUrl}
        onSubmit={login}
        register={register}
        formHandleSubmit={handleSubmit}
        errors={errors}
        language={language}
        reset={reset}
        isSubmitting={isSubmitting}
        getValues={getValues}
      />
    </SEO>
  );
};

const SignInContainer = () => {
  const reCaptchaKey = window?.env?.VITE_RECAPTCHA_KEY || import.meta.env.VITE_RECAPTCHA_KEY;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
      <SignInContainerInner />
    </GoogleReCaptchaProvider>
  );
};

export default React.memo(SignInContainer);
